import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Map,
  MapPin,
  Zap,
  Smartphone,
  AlertTriangle,
  Calendar,
  Gauge,
  History,
  Radio,
  ClipboardList,
  Settings,
  Shield,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  User,
  Users,
  Building2,
  BookOpen,
  Workflow,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import BrandLogo from './BrandLogo';
import LanguageSwitcher from './LanguageSwitcher';
import ImpersonationBanner from './ImpersonationBanner';
import BottomNavigation from './BottomNavigation';
import { isCgmPilotExperience } from '../config/cgmPilot';
import {
  FRONTEND_MODE_BADGE_KEY,
  IS_AWS_MODE,
  getAdminNavigationPath,
  isClientOnboardingAdminRole,
} from '../config/frontend';

interface NavItem {
  nameKey: string;
  descKey: string;
  href: string;
  icon: React.ElementType;
}

const roleLabelOf = (t: (key: string) => string, role: string | undefined | null): string => {
  switch ((role || '').trim().toLowerCase()) {
    case 'superadmin':
      return t('admin_users:roles.superadmin');
    case 'farmer':
      return t('agrisafeR1:nav.roleFarmer');
    case 'owner':
      return t('agrisafeR1:nav.roleOwner');
    case 'farm_manager':
      return t('agrisafeR1:nav.roleFarmManager');
    case 'admin':
    case 'org_admin':
    case 'coop_admin':
      return t('agrisafeR1:nav.roleAdmin');
    case 'technician':
      return t('agrisafeR1:nav.roleTechnician');
    case 'viewer':
      return t('agrisafeR1:nav.roleViewer');
    default:
      return role || '';
  }
};

const demoNavigation: NavItem[] = [
  { nameKey: 'nav:dashboard', descKey: 'nav:dashboardDesc', href: '/dashboard', icon: LayoutDashboard },
  { nameKey: 'nav:map', descKey: 'nav:mapDesc', href: '/map', icon: Map },
  { nameKey: 'nav:zones', descKey: 'nav:zonesDesc', href: '/zones', icon: Zap },
  { nameKey: 'nav:devices', descKey: 'nav:devicesDesc', href: '/devices', icon: Smartphone },
  { nameKey: 'nav:alerts', descKey: 'nav:alertsDesc', href: '/alerts', icon: AlertTriangle },
  { nameKey: 'nav:schedules', descKey: 'nav:schedulesDesc', href: '/schedules', icon: Calendar },
  { nameKey: 'nav:irrigationHistory', descKey: 'nav:irrigationHistoryDesc', href: '/irrigation-history', icon: History },
  { nameKey: 'nav:iot', descKey: 'nav:iotDesc', href: '/iot', icon: Radio },
  { nameKey: 'nav:digitalTwin', descKey: 'nav:digitalTwinDesc', href: '/digital-twin', icon: Workflow },
  { nameKey: 'nav:workOrders', descKey: 'nav:workOrdersDesc', href: '/work-orders', icon: ClipboardList },
  { nameKey: 'nav:settings', descKey: 'nav:settingsDesc', href: '/settings', icon: Settings },
  { nameKey: 'nav:documentation', descKey: 'nav:documentationDesc', href: '/documentation', icon: BookOpen },
];

const standardAwsNavigation: NavItem[] = [
  { nameKey: 'nav:dashboard', descKey: 'nav:dashboardDesc', href: '/dashboard', icon: LayoutDashboard },
  { nameKey: 'nav:iot', descKey: 'nav:iotDesc', href: '/iot', icon: Radio },
  { nameKey: 'nav:devices', descKey: 'nav:devicesDesc', href: '/devices', icon: Smartphone },
];

const cgmNavigation: NavItem[] = [
  { nameKey: 'agrisafeR1:nav.dashboard', descKey: 'agrisafeR1:nav.dashboardDesc', href: '/dashboard', icon: LayoutDashboard },
  { nameKey: 'agrisafeR1:nav.sectors', descKey: 'agrisafeR1:nav.sectorsDesc', href: '/sectors', icon: MapPin },
  { nameKey: 'agrisafeR1:nav.pilotage', descKey: 'agrisafeR1:nav.pilotageDesc', href: '/pilotage', icon: Radio },
  { nameKey: 'agrisafeR1:nav.pumping', descKey: 'agrisafeR1:nav.pumpingDesc', href: '/pumping', icon: Gauge },
  { nameKey: 'agrisafeR1:nav.equipment', descKey: 'agrisafeR1:nav.equipmentDesc', href: '/devices', icon: Smartphone },
];

const adminAwsNavigation: NavItem[] = [
  {
    nameKey: 'admin_common:title',
    descKey: 'admin_common:subtitle',
    href: '/admin',
    icon: Shield,
  },
  {
    nameKey: 'admin_common:sections.clients.title',
    descKey: 'admin_common:sections.clients.description',
    href: '/admin/clients',
    icon: Building2,
  },
  {
    nameKey: 'admin_common:sections.users.title',
    descKey: 'admin_common:sections.users.description',
    href: '/admin/users',
    icon: Users,
  },
];

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation(['common', 'nav', 'agrisafeR1', 'admin_common', 'admin_users']);
  const location = useLocation();
  const [impersonatingUser, setImpersonatingUser] = useState<any>(null);
  const adminNavigationPath = getAdminNavigationPath();
  const roleLabel = roleLabelOf(t, user?.role);

  useEffect(() => {
    if (IS_AWS_MODE) return;
    const impersonationData = localStorage.getItem('impersonating');
    if (impersonationData) {
      try {
        setImpersonatingUser(JSON.parse(impersonationData));
      } catch (error) {
        console.error('Error parsing impersonation data:', error);
      }
    }
  }, []);

  const handleStopImpersonation = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/impersonate/revert`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.access_token);
        localStorage.removeItem('impersonating');
        setImpersonatingUser(null);
        window.location.href = '/admin/users';
      }
    } catch (error) {
      console.error('Failed to stop impersonation:', error);
    }
  };

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const isCgm = isCgmPilotExperience(user.org_id, user.role);
  const isPlatformAdmin = IS_AWS_MODE && user.role === 'superadmin';
  const visibleNavigation = IS_AWS_MODE
    ? (isPlatformAdmin ? adminAwsNavigation : isCgm ? cgmNavigation : standardAwsNavigation)
    : demoNavigation;
  const displayName = (item: NavItem): string => t(item.nameKey);
  const displayDescription = (item: NavItem): string => t(item.descKey);
  const isNavActive = (item: NavItem): boolean =>
    location.pathname === item.href
    || (item.href !== '/admin' && location.pathname.startsWith(`${item.href}/`));
  const currentPage = visibleNavigation.find(isNavActive);
  const mainContentClasses = [
    'p-4 sm:p-6',
    IS_AWS_MODE && !isPlatformAdmin && 'pb-24 lg:pb-6',
  ].filter(Boolean).join(' ');

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      {!IS_AWS_MODE && impersonatingUser && (
        <ImpersonationBanner targetUser={impersonatingUser} onStopImpersonation={handleStopImpersonation} />
      )}

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-[var(--surface)] border-r border-[var(--border-light)] shadow-lg
        flex flex-col transform transition-transform duration-300 ease-in-out lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="shrink-0 flex items-center justify-between h-16 px-6 border-b border-[var(--border-light)]">
          <BrandLogo size="md" showText={true} />
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2.5 rounded-md hover:bg-[var(--gray-100)] min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label={t('agrisafeR1:nav.closeMenu')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="shrink-0 px-6 py-4 border-b border-[var(--border-light)]">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center shrink-0">
              <User className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--text-primary)] truncate">{user.name}</p>
              <p className="text-xs text-[var(--text-secondary)] truncate">{roleLabel}</p>
            </div>
          </div>
          {IS_AWS_MODE && isCgm && (
            <span className="inline-flex mt-3 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
              {t('agrisafeR1:nav.dripBadge')}
            </span>
          )}
          {!IS_AWS_MODE && (
            <span className="inline-flex mt-3 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
              {t(FRONTEND_MODE_BADGE_KEY)}
            </span>
          )}
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-2" data-testid="sidebar-nav">
          {visibleNavigation.map((item) => {
            const isActive = isNavActive(item);
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`
                  group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200
                  ${isActive
                    ? 'bg-gradient-primary text-white shadow-md'
                    : 'text-[var(--text-secondary)] hover:bg-[var(--gray-100)] hover:text-[var(--text-primary)]'
                  }
                `}
                data-testid={`nav-${item.href.replace(/^\//, '').replace(/\//g, '-')}`}
              >
                <item.icon className={`
                  w-5 h-5 mr-3 shrink-0 transition-colors
                  ${isActive ? 'text-white' : 'text-[var(--text-tertiary)] group-hover:text-[var(--text-primary)]'}
                `} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{displayName(item)}</div>
                  <div className={`text-xs mt-0.5 truncate ${isActive ? 'text-white/80' : 'text-[var(--text-tertiary)]'}`}>
                    {displayDescription(item)}
                  </div>
                </div>
              </Link>
            );
          })}

          {!IS_AWS_MODE && isClientOnboardingAdminRole(user?.role) && (
            <>
              <div className="my-4 border-t border-[var(--border-light)]"></div>
              <Link
                to={adminNavigationPath}
                onClick={() => setSidebarOpen(false)}
                className={`
                  group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200
                  ${location.pathname.startsWith('/admin')
                    ? 'bg-red-500 text-white shadow-md'
                    : 'text-[var(--text-secondary)] hover:bg-red-50 hover:text-red-600'
                  }
                `}
                data-testid="nav-admin"
              >
                <Shield className={`
                  w-5 h-5 mr-3 transition-colors
                  ${location.pathname.startsWith('/admin') ? 'text-white' : 'text-red-500 group-hover:text-red-600'}
                `} />
                <div className="flex-1">
                  <div className="font-medium">{t('nav:admin')}</div>
                  <div className={`text-xs mt-0.5 ${location.pathname.startsWith('/admin') ? 'text-white/80' : 'text-[var(--text-tertiary)]'}`}>
                    {t('nav:adminDesc')}
                  </div>
                </div>
              </Link>
            </>
          )}
        </nav>

        <div className="shrink-0 px-4 py-4 border-t border-[var(--border-light)]">
          <div className="space-y-3">
            <LanguageSwitcher />
            <div className="flex items-center justify-between">
              <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-[var(--gray-100)] transition-colors" data-testid="theme-toggle" title={t('nav:theme')}>
                {theme === 'light'
                  ? <Moon className="w-5 h-5 text-[var(--text-secondary)]" />
                  : <Sun className="w-5 h-5 text-[var(--text-secondary)]" />}
              </button>
              <button
                onClick={logout}
                className="flex items-center px-3 py-2 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--color-danger)] hover:bg-red-50 rounded-lg transition-colors"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('nav:logout')}
              </button>
            </div>
          </div>
        </div>
      </aside>

      <div className="lg:ml-64">
        <header className="bg-[var(--surface)] border-b border-[var(--border-light)] px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center min-w-0">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2.5 rounded-lg hover:bg-[var(--gray-100)] active:bg-[var(--gray-200)] mr-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
                data-testid="mobile-menu-button"
                aria-label={t('agrisafeR1:nav.openMenu')}
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg lg:text-xl font-semibold text-[var(--text-primary)] truncate">
                  {currentPage ? displayName(currentPage) : t('appName')}
                </h1>
                {currentPage && (
                  <p className="hidden sm:block text-sm text-[var(--text-secondary)] mt-0.5 truncate">
                    {displayDescription(currentPage)}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4 shrink-0">
              {!IS_AWS_MODE && (
                <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">{t(FRONTEND_MODE_BADGE_KEY)}</span>
              )}
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-[var(--text-primary)]">{user.name}</p>
                <p className="text-xs text-[var(--text-secondary)]">{roleLabel}</p>
              </div>
            </div>
          </div>
        </header>

        <main className={mainContentClasses}>
          <div className="max-w-7xl mx-auto"><Outlet /></div>
        </main>
      </div>

      {IS_AWS_MODE && !isPlatformAdmin && <BottomNavigation />}
    </div>
  );
};

export default Layout;
